/**
 * Get color for lifecycle stage
 * Single Responsibility: Color mapping logic
 */
export const getStageColor = (stage: string): string => {
  const colors: Record<string, string> = {
    ingredientImpact: "#6ba43d",
    cultivationImpact: "#83983e",
    processingImpact: "#3d8f66",
    packagingImpact: "#d49a35",
    transportImpact: "#3399cc",
    storageImpact: "#3e9895"
  };
  return colors[stage] || "#6b7280";
};

/**
 * Get tooltip text for lifecycle stage
 * Single Responsibility: Tooltip content logic
 */
export const getStageTooltip = (stage: string): string => {
  const tooltips: Record<string, string> = {
    packagingImpact:
      "Includes production of packaging materials and associated processing. Examples include bottles, cartons, plastics, and related energy use.",
    transportImpact:
      "Includes transportation between supply chain stages. This typically covers raw materials to your facility and distribution of finished products.",
    processingImpact:
      "Includes all operations.This covers mapped production steps (e.g. washing, pasteurization, drying), as well as energy, water, chemicals, and production losses. Chemical impacts include both their production and use.",
    ingredientImpact:
      "This includes the impact from the ingredients themselves. Some may be raw (like crops), others may already be processed.",
    cultivationImpact:
      "Includes cultivation activities associated with agricultural ingredients.This covers fertilizers, pesticides, irrigation, field energy, and direct field emissions.",
    storageImpact:
      "Includes internal and external storage across the supply chain. This covers storage duration, electricity use, temperature control, and refrigerant impacts."
  };
  return tooltips[stage] || "Lifecycle stage impact";
};

export const getStageSubtitle = (stage: string): string => {
  const subtitles: Record<string, string> = {
    packagingImpact: "Packaging materials and related processing",
    transportImpact: "Movement between supply chain stages",
    processingImpact: "All operations (machines, energy, water, chemicals, waste)",
    ingredientImpact: "Ingredients used in the product (raw or processed before production)",
    cultivationImpact: "Farming inputs and field operations for raw materials",
    storageImpact: "Storage conditions, duration, energy, and refrigerants"
  };
  return subtitles[stage] || stage.replace(/([a-z])([A-Z])/g, "$1 $2");
};

/**
 * Format date from ISO string
 * Single Responsibility: Date formatting logic
 */
export const formatCalculatedDate = (createdAt?: string): string => {
  if (!createdAt) return "Not calculated";

  const d = new Date(createdAt);
  if (isNaN(d.getTime())) return "Not calculated";

  return d.toISOString().slice(0, 19).replace("T", " ");
};

/**
 * Fixed display order for lifecycle stages, regardless of the order the
 * backend returns cycleStages keys in.
 */
const STAGE_DISPLAY_ORDER = [
  "ingredientimpact",
  "cultivationimpact",
  "processingimpact",
  "packagingimpact",
  "transportimpact",
  "storageimpact"
];

const normalizeStageKey = (key: string): string => key.toLowerCase().replace(/_/g, "");

/**
 * Process lifecycle stages with cultivation impact added to ingredient impact
 * Single Responsibility: Lifecycle stage transformation logic
 */
export const processLifecycleStages = (
  cycleStageData: any,
  NOT_APPLICABLE: string,
  humanize: (str: string) => string
) => {
  const stages = Object.entries(cycleStageData);

  return stages
    .map(([key, value]) => {
      let pctValue = value === NOT_APPLICABLE ? 0 : parseFloat(value as string) || 0;
      console.log("cycleStages", key);

      return {
        key,
        label: humanize(key),
        subtitle: getStageSubtitle(key),
        pct: pctValue,
        color: getStageColor(key),
        tooltip: getStageTooltip(key),
        drilldown: []
      };
    })
    .sort((a, b) => {
      const orderA = STAGE_DISPLAY_ORDER.indexOf(normalizeStageKey(a.key));
      const orderB = STAGE_DISPLAY_ORDER.indexOf(normalizeStageKey(b.key));
      return (
        (orderA === -1 ? STAGE_DISPLAY_ORDER.length : orderA) -
        (orderB === -1 ? STAGE_DISPLAY_ORDER.length : orderB)
      );
    })
    .map(({ key, ...stage }) => stage);
};
